// Pestaña «incidencias» de Entradas y aforo. Recibe el resultado de `clasificarIncidencias`:
// arriba lo que requiere acción, agrupado por función; abajo, plegado, el histórico de la migración.
import React from 'react';
import { formatCorto } from '../../utils/hora';

const money = amount => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount / 100);
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const fecha = valor => (valor === undefined ? 'fecha desconocida' : formatCorto(valor));
const ESTADOS = { PENDIENTE: 'Pendiente de pago', CONFIRMADA: 'Confirmada', CANCELADA: 'Cancelada', DEVOLUCION_PENDIENTE: 'Devolución pendiente', DEVUELTA: 'Devuelta', INCIDENCIA: 'Incidencia' };
const estado = o => `${ESTADOS[o.estado] || o.estado || 'Sin estado'}${o.bloqueada ? ' (bloqueada)' : ''}`;
const afectados = t => `${plural(t.pedidos, 'pedido', 'pedidos')} · ${plural(t.entradas, 'entrada', 'entradas')} · ${money(t.importe)}`;

function Pedido({ o, variosTipos, onVerCompras }) {
  return <li>
    <span><strong>{o.titulo}</strong> · {fecha(o.fecha)}</span>
    <span>{o.email || 'Sin email'}</span>
    <span>{plural(o.cantidad, 'entrada', 'entradas')} · {money(o.amount)}</span>
    <span>{estado(o)}</span>
    {variosTipos && <span>{o.tipo}</span>}
    <span className="ticket-hint">Referencia: {o._id}</span>
    <button type="button" aria-label={`Ver en Compras el pedido ${o._id}`}
      onClick={() => onVerCompras({ pedidoId: o._id, etiqueta: `pedido ${o._id}` })}>Ver en Compras</button>
  </li>;
}

function Tarjeta({ g, historico, onVerCompras }) {
  const titulo = `${g.titulo} · ${fecha(g.fecha)}`; const id = `incidencia-${g.clave.replace(/[^\w-]/g, '-')}`;
  const pedidos = <ul className="ticket-incidencia-pedidos" aria-label={`Pedidos afectados de ${titulo}`}>
    {g.pedidos.map(o => <Pedido key={o._id} o={o} variosTipos={g.tipos.length > 1} onVerCompras={onVerCompras} />)}
  </ul>;
  return <article className="ticket-card ticket-incidencia" aria-labelledby={id}>
    <h3 id={id}>{titulo}</h3>
    {/* En el histórico la discrepancia ya se explica arriba: no se repite en cada tarjeta. */}
    {!(historico && g.tipos.every(t => t.clave === 'discrepancia')) && <ul className="ticket-incidencia-tipos">{g.tipos.map(t => <li key={t.clave}>
      <strong>{t.etiqueta}</strong>{t.pedidos > 0 && ` · ${plural(t.pedidos, 'pedido', 'pedidos')}`}
      {t.textos.map(texto => <span key={texto} className="ticket-hint">{texto}</span>)}
    </li>)}</ul>}
    {g.incidenciaFuncion && !historico && <p>La función tiene esta incidencia: mientras no se resuelva (pestaña aforo), la web no vende entradas de esta función.</p>}
    <p>{g.pedidos.length ? afectados({ pedidos: g.pedidos.length, entradas: g.entradas, importe: g.importe }) : 'Sin pedidos afectados'}</p>
    {g.funcion && <button type="button" aria-label={`Ver compras de esta función: ${titulo}`}
      onClick={() => onVerCompras({ funcionId: g.funcionId, etiqueta: titulo })}>Ver compras de esta función</button>}
    {g.pedidos.length > 0 && (historico
      ? <details><summary>Ver {plural(g.pedidos.length, 'pedido', 'pedidos')}</summary>{pedidos}</details>
      : pedidos)}
  </article>;
}

/**
 * `resumen`: salida de `clasificarIncidencias`. `cargadas`: la última carga devolvió incidencias.
 * `onVerCompras({ funcionId?, pedidoId?, etiqueta })` abre la pestaña Compras filtrada.
 */
export default function Incidencias({ resumen, cargadas, loading, onVerCompras }) {
  const { accion, historico, totales, limiteAlcanzado } = resumen;
  return <section className="ticket-incidencias" aria-label="Incidencias">
    <h2>Requieren acción</h2>
    {!loading && !cargadas && <p role="alert">No se han podido cargar las incidencias. Pulsa «Actualizar».</p>}
    {!loading && cargadas && accion.length === 0 && <p>No hay incidencias que requieran acción.</p>}
    {accion.length > 0 && <p>{plural(totales.accion.funciones, 'función', 'funciones')} · {afectados(totales.accion)}</p>}
    {accion.map(g => <Tarjeta key={g.clave} g={g} onVerCompras={onVerCompras} />)}
    {limiteAlcanzado && <p className="ticket-hint">La API devuelve como máximo 500 compras con incidencia: puede haber más.</p>}
    {historico.length > 0 && <details className="ticket-historico">
      <summary>Histórico de la migración (informativo): {plural(totales.historico.funciones, 'función', 'funciones')} · {plural(totales.historico.pedidos, 'pedido', 'pedidos')}</summary>
      <p>Son funciones antiguas cuyos contadores de entradas de la web anterior no cuadraban con sus compras al migrar. Se cerraron y sus pedidos quedaron bloqueados como referencia. No afectan a la venta actual y no cuentan como pendientes. Revisarlas es opcional (casilla «resolver incidencia» de la pestaña aforo).</p>
      <p>También aparecen aquí los pedidos migrados de funciones pasadas con «conciliación pendiente»: el proveedor de pago ya no encuentra el cobro antiguo, algo esperable en compras de la web anterior.</p>
      {historico.map(g => <Tarjeta key={g.clave} g={g} historico onVerCompras={onVerCompras} />)}
    </details>}
  </section>;
}
